import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { cx } from '@platform/core/lib/cx';
import { routes } from '@platform/core/lib/routes';
import { catalogLabel, navGroup, shelves } from '@platform/navigation';
import type { ICategoryRailProps } from './ICategoryRailProps';
import { moduleSections } from './moduleSections';
import { usePins } from './usePins';
import { appRoutes } from './appRoutes';
import { useShellNav } from './useShellNav';

/**
 * Колонка категорій модулів (прототип v4, «Module categories» / ліва частина навігатора Ctrl+K): «Усі», «Закріплені», полиці («Робота»,
 * «Парламент»…) з акцентною рискою та групами під ними, «Система». Лічильник — кількість різних модулів категорії з урахуванням запиту;
 * категорії без модулів у просторі (і полиці без них) не показуються — «Загальне» з'являється з першим власним модулем цієї категорії.
 */
export function CategoryRail({ ws, q, group, onPick, compact = false }: ICategoryRailProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const navigate = useNavigate();
  const { catalog, parliament } = useShellNav(ws);
  const [pinned] = usePins(ws);
  const onSystem = (key: string) => navigate(key === 'administration' ? appRoutes.admin() : key === 'customization' ? appRoutes.customize() : routes.home());
  const base = { catalog, pinned, q, label, pinnedLabel: t('allModules.pinned'), systemLabel: t('allModules.system'), onSystem, parliament };
  const size = (key: string, query: string) => new Set(moduleSections({ ...base, q: query, group: key }).flatMap((s) => s.cards.map((c) => c.key.split('/')[0]))).size;
  const count = (key: string) => size(key, q);
  // категорії без модулів у просторі не показуються (прототип: cats — лише категорії, що мають модулі; «Загальне» — коли є власні модулі)
  const groupsOf = (keys: string[]) => keys.filter((key) => key === 'system' || (!!navGroup(key) && size(key, '') > 0));
  const item = (key: string, text: string, sub = false) => (
    <button key={key || 'all'} type="button" className={cx('am-rail__item', sub && !compact && 'is-sub', group === key && 'is-active')}
      aria-current={group === key ? 'true' : undefined} onClick={() => onPick(key)}>
      <span className="mnav__label">{text}</span><span className="am-rail__count">{count(key)}</span>
    </button>
  );
  return (
    <div className={cx('am-rail', compact && 'am-rail--compact')}>
      {item('', t('allModules.all'))}
      {item('pinned', t('allModules.pinned'))}
      {shelves().map((shelf) => ({ shelf, keys: groupsOf(shelf.groups) })).filter(({ keys }) => keys.length).map(({ shelf, keys }) => (
        <div key={shelf.key} role="group" aria-label={label(shelf.label)} className="am-rail__group">
          <div className="am-rail__shelf"><span className="am-rail__bar" aria-hidden="true" />{label(shelf.label)}</div>
          {keys.map((key) => {
            const g = navGroup(key);
            return key === 'system' ? item('system', t('allModules.system'), true) : g ? item(key, label(catalogLabel(g)), true) : null;
          })}
        </div>
      ))}
    </div>
  );
}
